import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormService, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {ApiService} from 'src/app/shared/services/api.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {PlacesService} from '../../../shared/services/places.service';
import {Place} from '../../../shared/models/place';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-place-permissions',
  templateUrl: './place-permissions.component.html',
  styleUrls: ['./place-permissions.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PlacePermissionsComponent implements OnInit {

  form: FormGroup;
  userId: number;
  submitted = false;
  placeId: string;
  selectedPlace: Place;
  showSeniority = false;
  seniorityOptions: {name: string, value: number}[] = [];

  constructor(private route: ActivatedRoute,
    public api: ApiService,
    private loader: LoaderService,
    public nav: NavigateService,
    private formBuilder: FormBuilder,
    private lang: LangService,
    public place: PlacesService,
    public forms: FormService) {
    this.userId = +this.route.snapshot.paramMap.get('userId');
    this.placeId = this.route.snapshot.paramMap.get('placeId');
  }

  ngOnInit() {
    this.seniorityOptions = this.api.env.seniorityOptions ? [
      {name: 'form.seniority.sen1', value: 1},
      {name: 'form.seniority.sen2', value: 2},
      {name: 'form.seniority.sen3', value: 3},
      {name: 'form.seniority.sen4', value: 4}
    ] : [
      {name: 'form.seniority.ownPlace', value: 0},
      {name: 'form.seniority.allPlaces', value: 1}
    ];

    this.loader.loading(true);
    this.place.getPermissionPlace(this.userId, this.placeId).then(() => {
      this.place.getPlace(this.placeId).then((p: Place) => {
        this.selectedPlace = p;
      });
      this.setForm();
      this.loader.loading(false);
    }).catch(() => {
      this.loader.loading(false);
    });
  }

  setForm() {
    const canShowSeniority = this.api.env.showSeniority || !this.api.env.useGuichet;
    if(this.place?.userPlace?.seniority !== undefined && (this.place?.userPlace?.allowControl || this.place?.userPlace?.allowExtension)) {
      if(canShowSeniority){
        this.showSeniority = true;
      }
    }
    this.form = this.formBuilder.group({
      perm1: [this.api.env.allowOnlyOnePerm ? this.place?.userPlace?.seniority === 1 : this.place?.userPlace?.allowCreate, []],
      perm2: [this.api.env.allowOnlyOnePerm ? this.place?.userPlace?.seniority === 2 : this.place?.userPlace?.allowConsume, []],
      perm3: [this.api.env.allowOnlyOnePerm ? this.place?.userPlace?.seniority === 3 : this.place?.userPlace?.allowControl, []],
      perm4: [this.api.env.allowOnlyOnePerm ? this.place?.userPlace?.seniority === 4 : this.place?.userPlace?.allowAdmin, []],
      perm5: [this.place?.userPlace?.allowExtension, []],
      seniority: [this.place?.userPlace?.seniority, [
        this.api.env.allowOnlyOnePerm ? Validators.pattern('^[1-4]$') : Validators.pattern('^[0-3]$'),
        ...(this.place?.userPlace?.seniority !== undefined ? [Validators.required] : [])
      ]]
    });

    const updateSen = (value: boolean) => {
      const seniority = this.form.get('seniority');
      if(value === true) {
        this.showSeniority = canShowSeniority;
        seniority.setValue(this.place?.userPlace?.seniority);
        seniority.setValidators([Validators.pattern('^[0-3]$'), Validators.required]);
      } else {
        this.showSeniority = false;
        seniority.clearValidators();
      }
      seniority.updateValueAndValidity();
    };

    this.form.get('perm3').valueChanges.subscribe(value => updateSen(value));
    this.form.get('perm5').valueChanges.subscribe(value => updateSen(value));

    if(this.api.env.allowOnlyOnePerm) {
      const permSeniorityMap: {[key: string]: number} = {perm1: 1, perm2: 2, perm3: 3, perm4: 4};
      const radioPerms = ['perm1', 'perm2', 'perm3', 'perm4'];
      radioPerms.forEach(perm => {
        this.form.get(perm).valueChanges.subscribe(value => {
          if(value === true) {
            radioPerms.filter(p => p !== perm).forEach(other => {
              this.form.get(other).setValue(false, {emitEvent: false});
            });
            this.form.get('seniority').setValue(permSeniorityMap[perm], {emitEvent: false});
          } else if(radioPerms.every(p => !this.form.get(p).value)) {
            this.form.get('seniority').setValue(null, {emitEvent: false});
          }
        });
      });
    }
  }

  updatePermissions() {
    if(this.forms.getFormErrors(this.form).length < 1) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('ask.place.updatePermissions'), btnLabel: this.lang.transform('global.confirm')}).then((done: boolean) => {
        if(done) {
          this.loader.loading(true);
          let createPlaceIds: string[] = [];
          let consumePlaceIds: string[] = [];
          let controlPlaceIds: string[] = [];
          let adminPlaceIds: string[] = [];
          let extensionPlaceIds: string[] = [];

          this.place?.user?.links?.forEach(item => {
            item.allowCreate ? createPlaceIds.push(item.place_id) : undefined;
            item.allowConsume ? consumePlaceIds.push(item.place_id) : undefined;
            item.allowControl ? controlPlaceIds.push(item.place_id) : undefined;
            item.allowAdmin ? adminPlaceIds.push(item.place_id) : undefined;
            item.allowExtension ? extensionPlaceIds.push(item.place_id) : undefined;
          });

          if(!this.api.env.allowOnlyOnePerm) {
            this.form.get('perm1')?.value ? createPlaceIds.push(this.placeId) : createPlaceIds = createPlaceIds.filter(x => x !== this.placeId);
            this.form.get('perm2')?.value ? consumePlaceIds.push(this.placeId) : consumePlaceIds = consumePlaceIds.filter(x => x !== this.placeId);
            this.form.get('perm3')?.value ? controlPlaceIds.push(this.placeId) : controlPlaceIds = controlPlaceIds.filter(x => x !== this.placeId);
            this.form.get('perm4')?.value ? adminPlaceIds.push(this.placeId) : adminPlaceIds = adminPlaceIds.filter(x => x !== this.placeId);
          }
          this.form.get('perm5')?.value ? extensionPlaceIds.push(this.placeId) : extensionPlaceIds = extensionPlaceIds.filter(x => x !== this.placeId);

          let seniorityValue = parseInt(this.form.get('seniority')?.value, 10);
          if(this.api.env.useGuichet && !this.api.env.showSeniority && !this.api.env.seniorityOptions) {
            seniorityValue = 3;
          }

          return this.api.places(this.userId, createPlaceIds, consumePlaceIds, controlPlaceIds, adminPlaceIds, extensionPlaceIds, seniorityValue).then(() => this.place.getPermissionPlace(this.userId, this.placeId).then(() =>{
            this.loader.loading(true, {type: 'info', message: this.lang.transform('place.updatePermissions.success')});
          })).catch(err => {
            this.loader.loading(true, {type: 'error', message: err || this.lang.transform('err.place.updatePermissions')});
          });
        }
      });
    } else {
      this.submitted = true;
    }
  }

  removePlace() {
    this.loader.loading(true);
    const createPlaceIds: string[] = [];
    const consumePlaceIds: string[] = [];
    const controlPlaceIds: string[] = [];
    const adminPlaceIds: string[] = [];
    const extensionPlaceIds: string[] = [];
    let maxSeniority = 1;
    this.place?.user?.links?.forEach(item => {
      if(item.place_id !== this.place?.userPlace?.place_id) {
        item.allowCreate ? createPlaceIds.push(item.place_id) : undefined;
        item.allowConsume ? consumePlaceIds.push(item.place_id) : undefined;
        item.allowControl ? controlPlaceIds.push(item.place_id) : undefined;
        item.allowAdmin ? adminPlaceIds.push(item.place_id) : undefined;
        item.allowExtension ? extensionPlaceIds.push(item.place_id) : undefined;
        if(item.seniority > maxSeniority) maxSeniority = item.seniority;
      }
    });
    if(this.place?.user?.links.length > 1) {
      this.loader.loading(true, {type: 'info', message: this.lang.transform('place.delete.link.ask'), btnLabel: this.lang.transform('global.confirm')}).then((done: boolean) => {
        if(done) {
          this.api.places(this.place?.user?.id, createPlaceIds, consumePlaceIds, controlPlaceIds, adminPlaceIds, extensionPlaceIds, maxSeniority).then(() => {
            this.loader.loading(true, {type: 'info', message: this.lang.transform('place.delete.link.done')}).then(() => {
              this.nav.to('user/'+this.userId+'/places-choice');
            });
          }).catch((err) => {
            this.loader.loading(true, {type: 'error', message: err});
          });
        }
      });
    } else {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('lastPlace.delete'), btnLabel: this.lang.transform('global.confirm')}).then((done: boolean) => {
        if(done) {
          this.api.places(this.place?.user?.id, createPlaceIds, consumePlaceIds, controlPlaceIds, adminPlaceIds, extensionPlaceIds, maxSeniority).then(() => {
            this.loader.loading(true, {type: 'info', message: this.lang.transform('place.delete.link.done')}).then(() => {
              this.nav.to('user/'+this.userId+'/places-choice');
            });
          }).catch((err) => {
            this.loader.loading(true, {type: 'error', message: err});
          });
        }
      });
    }
  }
  protected readonly environment = environment;
}
